import { useState } from 'react'

import { useI18n } from '../../../../shared/i18n/useI18n.js'

import { ChevronUp } from '../../../../shared/ui/icons/index.js'

import styles from './CollapsiblePane.module.scss'

/**
 * CollapsiblePane — reusable collapsible section with a header.
 *
 * Usage:
 *   <CollapsiblePane title="Opis lokalu" startCollapsed={false}>
 *     {content}
 *   </CollapsiblePane>
 *
 * @param {string}    props.title         Header label
 * @param {boolean}   [props.startCollapsed=false]
 * @param {ReactNode} props.children      Body content
 * @param {string}    [props.collapseAriaLabel]
 * @param {string}    [props.expandAriaLabel]
 */
export function CollapsiblePane({
  title,
  startCollapsed = false,
  children,
  collapseAriaLabel = null,
  expandAriaLabel = null,
}) {
  const { t } = useI18n()
  const [isExpanded, setIsExpanded] = useState(!startCollapsed)

  const ariaLabel = isExpanded ? t(collapseAriaLabel ?? "common.show") : t(expandAriaLabel ?? "common.hide")

  return (
    <div className={styles.collapsablePaneHSegment}>
      <button
        className={styles.headerButton}
        onClick={() => setIsExpanded((v) => !v)}
        aria-expanded={isExpanded}
        aria-label={ariaLabel}
      >
        <span className={styles.title}>{title}</span>
        <span
          className={styles.chevron}
          style={{
            transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
            transition: 'transform 0.25s ease',
          }}
        >
          <ChevronUp />
        </span>
      </button>

      <div
        className={`${styles.paneHolder} ${isExpanded ? styles.body_expanded : styles.body_collapsed}`}
      >
        {children}
      </div>
    </div>
  )
}
